import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from './common/svgs/svgs';
import { MATCH_STYLE_OPTIONS, MatchStyleVariant } from './peer-listener-match-hint';

type MatchStyleTrialDropdownProps = {
  selectedValue: MatchStyleVariant;
  onSelect: (value: MatchStyleVariant) => void;
  className?: string;
};

export const MatchStyleTrialDropdown = ({ selectedValue, onSelect, className = '' }: MatchStyleTrialDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = MATCH_STYLE_OPTIONS.find((option) => option.value === selectedValue);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = useCallback(
    (value: MatchStyleVariant) => {
      onSelect(value);
      setIsOpen(false);
    },
    [onSelect],
  );

  return (
    <div ref={dropdownRef} className={`relative inline-flex flex-col items-start ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="border-1 bg-button-secondary hover:bg-button-secondary-hover inline-flex items-center justify-center gap-1.5 rounded-full px-7 py-3"
      >
        <span className="font-albert-sans text-primary text-base font-medium leading-5">
          {selectedOption?.label || 'Match style'}
        </span>
        <span className="flex h-5 w-5 items-center justify-center">
          <ChevronDownIcon className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {isOpen ? (
        <div
          role="listbox"
          className="absolute top-full z-10 mt-2 max-h-96 w-64 overflow-y-auto rounded-lg border border-gray-300 bg-white"
        >
          {MATCH_STYLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === selectedValue}
              onClick={() => handleOptionClick(option.value)}
              className={`hover:bg-button-secondary focus:bg-button-secondary flex w-full items-center px-4 py-2 text-left focus:outline-none ${
                option.value === selectedValue ? 'bg-button-secondary' : ''
              }`}
            >
              <span className="font-albert-sans text-primary text-sm font-medium">{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
